import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { importarAceptados } from "./ApiServices";
import { formatCuit, formatMoneda } from "../pago_proveedores/utils";
import { labelEstado } from "./cheques.constants";
import {
  colorTipoOmision,
  filaOmitido,
  libradorOmitido,
  listaOmitidos,
  motivoOmitido,
  omitidoTieneCodigo,
  refOmitido,
  resumenOmitidos,
} from "./importarOmitidos";

const ACCEPT =
  ".csv,.xlsx,.xls,.txt,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const EXTENSIONES_OK = [".csv", ".xlsx", ".xls", ".txt"];
const PAGE_SIZE = 10;

const esArchivoPermitido = (file) => {
  if (!file?.name) return false;
  const nombre = file.name.toLowerCase();
  return EXTENSIONES_OK.some((ext) => nombre.endsWith(ext));
};

const paginar = (lista, page, pageSize) => {
  const inicio = page * pageSize;
  return lista.slice(inicio, inicio + pageSize);
};

const listaDe = (resultado, key) =>
  Array.isArray(resultado?.[key]) ? resultado[key] : [];

const SECCIONES = [
  { key: "matched", titulo: "Match OK → cartera", color: "success" },
  { key: "ya_en_cartera", titulo: "Ya en cartera", color: "info" },
  { key: "banco_sin_autorizacion_cc", titulo: "Banco sin autorización CC", color: "error" },
  { key: "desconocidos", titulo: "No existen en Netizar", color: "warning" },
  { key: "sin_asignacion", titulo: "Sin cliente/proveedor", color: "warning" },
  { key: "autorizados_sin_match", titulo: "Autorizados CC sin match", color: "warning" },
];

function TablaBucket({ rows, page, onPageChange }) {
  const pagina = paginar(rows, page, PAGE_SIZE);
  return (
    <>
      <TableContainer
        sx={{ maxHeight: 220, border: "1px solid #e0e0e0", borderRadius: 1 }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {["N° / ID cheque", "Estado", "Detalle"].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagina.map((row, index) => (
              <TableRow
                key={
                  row.id ??
                  row.id_cheque ??
                  `${row.nro_cheque}-${page * PAGE_SIZE + index}`
                }
              >
                <TableCell>
                  <Typography variant="body2">
                    {row.nro_cheque || row.id || "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.id_cheque || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  {labelEstado(row.estado)}
                  {row.importe != null ? ` · ${formatMoneda(row.importe)}` : ""}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
                    {row.motivo ||
                      row.detalle ||
                      (Array.isArray(row.errores) ? row.errores.join(". ") : "") ||
                      row.mensaje ||
                      "—"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length > PAGE_SIZE ? (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_e, newPage) => onPageChange(newPage)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count}`
          }
        />
      ) : null}
    </>
  );
}

export default function ModalImportarAceptados({
  open,
  onClose,
  onImportacionOk,
}) {
  const inputRef = useRef(null);
  const [archivo, setArchivo] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [modo, setModo] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState("");
  const [marcarFaltantes, setMarcarFaltantes] = useState(false);
  const [pages, setPages] = useState({});
  const [pageOmitidos, setPageOmitidos] = useState(0);
  const [filtroOmitido, setFiltroOmitido] = useState("");

  const resetEstado = useCallback(() => {
    setArchivo(null);
    setResultado(null);
    setModo(null);
    setError("");
    setDragOver(false);
    setMarcarFaltantes(false);
    setPages({});
    setPageOmitidos(0);
    setFiltroOmitido("");
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  useEffect(() => {
    if (!open) resetEstado();
  }, [open, resetEstado]);

  const asignarArchivo = useCallback((file) => {
    if (!file) return;
    if (!esArchivoPermitido(file)) {
      setError("Formato no permitido. Use .csv, .xlsx, .xls o .txt");
      return;
    }
    setArchivo(file);
    setResultado(null);
    setModo(null);
    setError("");
    setPages({});
    setPageOmitidos(0);
    setFiltroOmitido("");
  }, []);

  const buckets = useMemo(() => {
    if (!resultado) return {};
    return SECCIONES.reduce((acc, s) => {
      acc[s.key] = listaDe(resultado, s.key);
      return acc;
    }, {});
  }, [resultado]);

  const totalMatch = buckets.matched?.length ?? 0;

  const omitidos = useMemo(() => listaOmitidos(resultado), [resultado]);
  const resumen = useMemo(() => resumenOmitidos(resultado), [resultado]);
  const omitidosFiltrados = useMemo(
    () =>
      filtroOmitido
        ? omitidos.filter((row) => omitidoTieneCodigo(row, filtroOmitido))
        : omitidos,
    [omitidos, filtroOmitido]
  );
  const omitidosPagina = useMemo(
    () => paginar(omitidosFiltrados, pageOmitidos, PAGE_SIZE),
    [omitidosFiltrados, pageOmitidos]
  );

  const handleArchivo = (e) => {
    asignarArchivo(e.target.files?.[0] ?? null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!procesando) setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (procesando) return;
    asignarArchivo(e.dataTransfer?.files?.[0] ?? null);
  };

  const ejecutarUpload = async (confirmar) => {
    if (!archivo) {
      setError("Seleccione o arrastre un archivo CSV o Excel");
      return;
    }

    setProcesando(true);
    setError("");
    setResultado(null);
    setModo(null);
    setPages({});
    setPageOmitidos(0);
    setFiltroOmitido("");

    try {
      const data = await importarAceptados(archivo, {
        confirmar,
        marcarFaltantes,
      });
      setResultado(data);
      setModo(confirmar ? "aplicado" : "preview");
      setPages({});
      if (confirmar) onImportacionOk?.(data);
    } catch (err) {
      setError(err.message || "Error al procesar el archivo");
    } finally {
      setProcesando(false);
    }
  };

  const puedeConfirmar =
    Boolean(archivo) && modo === "preview" && !procesando;

  return (
    <Dialog open={open} onClose={procesando ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Conciliar aceptados del banco</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Subí el Excel de cheques que el banco aceptó. Se compara por ID de
          cheque contra lo autorizado por Cuentas Corrientes. Un match correcto
          pasa el cheque a cartera (Disponible).
        </Alert>

        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !procesando && inputRef.current?.click()}
          sx={{
            mb: 2,
            py: 1.25,
            px: 2,
            border: "2px dashed",
            borderColor: dragOver ? "#1F618D" : archivo ? "#27AE60" : "#BDC3C7",
            borderRadius: 1.5,
            bgcolor: dragOver ? "rgba(31, 97, 141, 0.08)" : "#FAFBFC",
            cursor: procesando ? "default" : "pointer",
            transition: "border-color 0.15s, background-color 0.15s",
            "&:hover": procesando
              ? undefined
              : { borderColor: "#1F618D", bgcolor: "rgba(31, 97, 141, 0.04)" },
          }}
        >
          <input
            ref={inputRef}
            type="file"
            hidden
            accept={ACCEPT}
            onChange={handleArchivo}
            disabled={procesando}
          />
          {archivo ? (
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <InsertDriveFileIcon sx={{ fontSize: 28, color: "#27AE60" }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={600} color="#1F618D" noWrap title={archivo.name}>
                  {archivo.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(archivo.size / 1024).toFixed(1)} KB — arrastrá otro o clic para cambiar
                </Typography>
              </Box>
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CloudUploadIcon sx={{ fontSize: 28, color: "#1F618D" }} />
              <Box>
                <Typography variant="body2" fontWeight={600} color="#1F618D">
                  Arrastrá el Excel aquí o hacé clic
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  .csv, .xlsx, .xls, .txt
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>

        <FormControlLabel
          sx={{ mb: 2 }}
          control={
            <Checkbox
              size="small"
              checked={marcarFaltantes}
              onChange={(e) => setMarcarFaltantes(e.target.checked)}
              disabled={procesando}
            />
          }
          label="Marcar autorizados CC ausentes en este Excel como inconsistentes"
        />

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {modo === "aplicado" ? (
          <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 2 }}>
            Conciliación confirmada.
            {totalMatch > 0 ? ` ${totalMatch} cheque(s) pasaron a cartera.` : ""}
          </Alert>
        ) : null}

        {resultado ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {SECCIONES.map((s) => {
              const n = buckets[s.key]?.length ?? 0;
              return (
                <Chip
                  key={s.key}
                  label={`${s.titulo}: ${n}`}
                  size="small"
                  color={n > 0 ? s.color : "default"}
                  variant="outlined"
                />
              );
            })}
          </Stack>
        ) : null}

        {SECCIONES.map((s) => {
          const rows = buckets[s.key] || [];
          if (!rows.length) return null;
          return (
            <Box key={s.key} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                {s.titulo} ({rows.length})
              </Typography>
              <TablaBucket
                rows={rows}
                page={pages[s.key] || 0}
                onPageChange={(newPage) =>
                  setPages((prev) => ({ ...prev, [s.key]: newPage }))
                }
              />
            </Box>
          );
        })}

        {omitidos.length > 0 ? (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              No se pueden importar ({omitidos.length})
            </Typography>
            {resumen.length > 0 ? (
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 1.5 }}
              >
                {resumen.map((item) => {
                  const codigo = item.codigo || item.campo || item.mensaje;
                  const activo = filtroOmitido === codigo;
                  return (
                    <Chip
                      key={codigo}
                      size="small"
                      variant={activo ? "filled" : "outlined"}
                      color={colorTipoOmision(item.tipo)}
                      label={`${item.mensaje || item.codigo}: ${item.total ?? 0}`}
                      onClick={() => {
                        setFiltroOmitido((prev) =>
                          prev === codigo ? "" : codigo
                        );
                        setPageOmitidos(0);
                      }}
                    />
                  );
                })}
              </Stack>
            ) : null}
            <TableContainer
              sx={{ maxHeight: 280, border: "1px solid #e0e0e0", borderRadius: 1 }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["Fila / ref.", "Cheque", "CUIT / librador", "Motivo"].map(
                      (h) => (
                        <TableCell
                          key={h}
                          sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}
                        >
                          {h}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {omitidosPagina.map((row, index) => {
                    const absIndex = pageOmitidos * PAGE_SIZE + index;
                    const { etiqueta, cuit } = libradorOmitido(row);
                    const ref = refOmitido(row);
                    return (
                      <TableRow
                        key={`${filaOmitido(row, absIndex)}-${row.id_cheque || absIndex}`}
                      >
                        <TableCell>
                          <Typography variant="body2">
                            Fila {filaOmitido(row, absIndex)}
                          </Typography>
                          {ref ? (
                            <Typography variant="caption" color="text.secondary">
                              {ref}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.nro_cheque || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.id_cheque || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{etiqueta}</Typography>
                          {cuit ? (
                            <Typography variant="caption" color="text.secondary">
                              {formatCuit(cuit)}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="error"
                            sx={{ whiteSpace: "normal" }}
                          >
                            {motivoOmitido(row)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={omitidosFiltrados.length}
              page={pageOmitidos}
              onPageChange={(_e, newPage) => setPageOmitidos(newPage)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={procesando}>
          Cerrar
        </Button>
        <Button
          variant="outlined"
          startIcon={
            procesando && modo !== "aplicado" ? (
              <CircularProgress size={16} />
            ) : (
              <VisibilityIcon />
            )
          }
          disabled={!archivo || procesando}
          onClick={() => ejecutarUpload(false)}
        >
          Analizar
        </Button>
        <Button
          variant="contained"
          startIcon={
            procesando && modo === "aplicado" ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CheckCircleOutlineIcon />
            )
          }
          disabled={!puedeConfirmar}
          onClick={() => ejecutarUpload(true)}
        >
          Confirmar conciliación
        </Button>
      </DialogActions>
    </Dialog>
  );
}
